import { type ReactNode } from 'react';
import type { GroupBonusTier } from '../utils/tier-position';
import { tierMarkPct, currentTierIndex } from '../utils/tier-position';

type Props = { tiers: GroupBonusTier[]; avg: number };

function Dot({ pos, filled }: { pos: number; filled: boolean }): ReactNode {
  const cls = filled
    ? 'bg-brand-red ring-brand-red/30'
    : 'bg-surface-secondary ring-white/[0.04]';
  return (
    <span
      className={`absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ${cls}`}
      style={{ left: `${pos}%` }}
    />
  );
}

function Mark({
  pos,
  amount,
  active,
}: {
  pos: number;
  amount: number;
  active: boolean;
}): ReactNode {
  const color = active ? 'text-brand-red' : 'text-text-tertiary';
  return (
    <span
      className={`absolute -translate-x-1/2 text-[10px] tabular-nums ${color}`}
      style={{ left: `${pos}%` }}
    >
      ${amount}
    </span>
  );
}

function Track({
  tiers,
  reached,
}: {
  tiers: GroupBonusTier[];
  reached: number;
}): ReactNode {
  const n = tiers.length;
  const fillTo = reached >= 0 ? tierMarkPct(reached, n) : 0;
  return (
    <div className="relative h-2.5">
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-white/[0.06]" />
      <div
        className="absolute left-0 top-1/2 h-px -translate-y-1/2 bg-brand-red/80 transition-all duration-500"
        style={{ width: `${fillTo}%` }}
      />
      {tiers.map((_, i) => (
        <Dot key={i} pos={tierMarkPct(i, n)} filled={i <= reached} />
      ))}
    </div>
  );
}

export function TierTrack({ tiers, avg }: Props): ReactNode {
  if (tiers.length === 0) return null;
  const reached = currentTierIndex(tiers, avg);
  const n = tiers.length;
  return (
    <div>
      <Track tiers={tiers} reached={reached} />
      <div className="relative mt-sm h-3">
        {tiers.map((t, i) => (
          <Mark
            key={i}
            pos={tierMarkPct(i, n)}
            amount={t.amount}
            active={i <= reached}
          />
        ))}
      </div>
    </div>
  );
}
